import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";

import { addCartLines, addSpecialCartLine, createCart } from "../api/carts";
import {
  fetchPurchaseOrder,
  fetchPurchaseOrders,
  purchaseOrderQueryKey,
  purchaseOrdersQueryKey,
  type PurchaseOrder,
} from "../api/purchaseOrders";
import { ApiError } from "../lib/api";
import { mailtoHref, orderCsv, orderMail } from "../lib/cartExport";
import { cleanDescription } from "../lib/description";
import { downloadOfferCsv } from "../lib/offerCsv";
import { formatDateShort, formatEuro, formatQty } from "../lib/format";
import { useDebouncedValue } from "../lib/useDebouncedValue";

const BUTTON = "h-8 rounded-md border border-slate-300 bg-white px-2.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50";

/** Orders the buyer has confirmed, newest first (#554). */
export default function PurchaseOrdersTab({ scope, onCartCreated }: { scope: "mine" | "company"; onCartCreated: (cartId: string) => void }) {
  const [search, setSearch] = useState("");
  const debounced = useDebouncedValue(search, 250);
  const query = useQuery({
    queryKey: purchaseOrdersQueryKey(scope, debounced.trim()),
    queryFn: () => fetchPurchaseOrders(scope, debounced),
  });

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Bestellnummer, Artikel oder Kostenstelle …"
          aria-label="Bestellungen durchsuchen"
          className="h-8 w-72 max-w-full rounded-md border border-slate-300 px-3 text-sm"
        />
      </div>
      {query.isLoading ? <p className="text-sm text-slate-500">Lade Bestellungen …</p> : null}
      {query.isError ? (
        <p role="alert" className="text-sm text-rose-700">
          {query.error instanceof ApiError ? query.error.message : "Die Bestellungen konnten nicht geladen werden."}
        </p>
      ) : null}
      {query.data?.items.length === 0 ? (
        <p className="rounded-lg border border-dashed border-slate-300 p-6 text-sm text-slate-600">
          {debounced.trim() ? "Keine Bestellung passt zur Suche." : "Noch keine Bestellung. Aus einem Warenkorb wird über „Bestellung bestätigen“ eine."}
        </p>
      ) : null}
      {query.data?.items.map((order) => (
        <OrderRow key={order.order_id} order={order} showOwner={scope === "company"} onCartCreated={onCartCreated} />
      ))}
    </div>
  );
}

function OrderRow({ order, showOwner, onCartCreated }: { order: PurchaseOrder; showOwner: boolean; onCartCreated: (cartId: string) => void }) {
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();
  const detail = useQuery({
    queryKey: purchaseOrderQueryKey(order.order_id),
    queryFn: () => fetchPurchaseOrder(order.order_id),
    enabled: open,
  });
  // "Nochmal in den Warenkorb": same articles and quantities at today's prices.
  const reorder = useMutation({
    mutationFn: async () => {
      const lines = detail.data?.lines ?? (await fetchPurchaseOrder(order.order_id)).lines;
      const cart = await createCart(`Nochmal ${order.reference}`);
      const articles = lines.filter((line) => !line.is_special && line.article_group_key);
      let filled = articles.length
        ? await addCartLines(cart, articles.map((line) => ({ article_group_key: line.article_group_key!, qty: Number(line.qty) })))
        : cart;
      // Special positions have no article, so they are added again as what they
      // are: the agreed text, quantity and price (#556).
      for (const line of lines.filter((item) => item.is_special)) {
        filled = await addSpecialCartLine(filled, {
          description: cleanDescription(line.description) ?? "Sonderposition",
          qty: Number(line.qty),
          uom: line.uom,
          agreed_price: Number(line.unit_price ?? 0),
          supplier: order.supplier,
        });
      }
      return filled;
    },
    onSuccess: (cart) => {
      void queryClient.invalidateQueries({ queryKey: ["carts", "list"] });
      onCartCreated(cart.cart_id);
    },
  });

  return (
    <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 px-4 py-3 text-sm">
        <span className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <button type="button" onClick={() => setOpen((value) => !value)} className="font-semibold text-slate-900 hover:underline" aria-expanded={open}>
            {order.reference}
          </button>
          <span className="text-slate-600">{formatDateShort(order.ordered_at)}</span>
          <span className="text-slate-900">{order.supplier}</span>
          <span className="text-slate-600">{order.cost_center.label}</span>
          {showOwner ? <span className="text-slate-500">{order.owner.name}</span> : null}
        </span>
        <span className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span className="rounded bg-slate-100 px-1.5 py-0.5 text-xs font-medium text-slate-700">{order.status_label}</span>
          <span className="font-semibold text-slate-900">{formatEuro(order.total)}</span>
          <span className="text-xs text-slate-500">{order.line_count} {order.line_count === 1 ? "Position" : "Positionen"}</span>
        </span>
      </div>
      {open ? (
        <div className="border-t border-slate-100 px-4 py-3">
          {detail.isLoading ? <p className="text-sm text-slate-500">Lade Bestellung …</p> : null}
          {detail.data ? (
            <>
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-slate-500">
                  <tr>
                    <th className="py-1 font-medium">Artikel</th>
                    <th className="w-24 py-1 text-right font-medium">Menge</th>
                    <th className="w-28 py-1 text-right font-medium">Preis</th>
                    <th className="w-28 py-1 text-right font-medium">Vorgabe</th>
                    <th className="w-28 py-1 text-right font-medium">Summe</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.data.lines.map((line) => (
                    <tr key={line.position} className="border-t border-slate-100">
                      <td className="py-1.5">
                        {line.is_special || !line.article_group_key ? (
                          <>
                            <span className="text-slate-900">{cleanDescription(line.description) || "Sonderposition"}</span>
                            <span className="mt-0.5 block text-xs font-medium text-amber-800">Sonderposition</span>
                          </>
                        ) : (
                          <>
                            <Link className="text-slate-900 hover:underline" to={`/articles/${encodeURIComponent(line.article_group_key)}`}>
                              {cleanDescription(line.description) || line.article_code || line.article_group_key}
                            </Link>
                            <span className="block text-xs text-slate-500">{line.article_code}</span>
                          </>
                        )}
                      </td>
                      <td className="py-1.5 text-right">{formatQty(line.qty)} {line.uom ?? ""}</td>
                      <td className="py-1.5 text-right">{formatEuro(line.unit_price)}</td>
                      <td className="py-1.5 text-right">{formatEuro(line.target_price)}</td>
                      <td className="py-1.5 text-right font-medium">{formatEuro(line.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <a
                  href={mailtoHref(orderMail(detail.data).subject, orderMail(detail.data).body)}
                  className="inline-flex h-8 items-center rounded-md border border-slate-300 bg-white px-2.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                >
                  Bestellung per E-Mail
                </a>
                <button type="button" className={BUTTON} onClick={() => downloadOfferCsv(orderCsv(detail.data!), `${order.reference}.csv`)}>
                  Export für Excel
                </button>
                <button type="button" className={BUTTON} disabled={reorder.isPending} onClick={() => reorder.mutate()}>
                  {reorder.isPending ? "Lege Warenkorb an …" : "Nochmal in den Warenkorb"}
                </button>
                <span className="text-xs text-slate-500">
                  Bestellnummer {order.reference} dem Großhändler mitgeben – damit findet die Auftragsbestätigung ihre Bestellung.
                </span>
              </div>
              {reorder.isError ? (
                <p role="alert" className="mt-2 text-xs text-rose-700">
                  {reorder.error instanceof ApiError ? reorder.error.message : "Der Warenkorb konnte nicht angelegt werden."}
                </p>
              ) : null}
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
